import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  Boxes,
  Briefcase,
  CalendarDays,
  CalendarHeart,
  ClipboardList,
  LayoutDashboard,
  PieChart,
  Settings,
  ShieldCheck,
  Stethoscope,
  TrendingUp,
  UserCircle,
  UserRound,
  Users,
} from 'lucide-react'
import { ROLES, type Role } from '@/constants'

export interface NavItem {
  key: string
  label: string
  path: string
  icon: LucideIcon
  roles?: Role[]
  children?: NavItem[]
}

export const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA],
  },
  {
    key: 'agenda',
    label: 'Agenda',
    path: '/agenda',
    icon: CalendarDays,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL],
  },
  {
    key: 'appointments',
    label: 'Turnos',
    path: '/turnos',
    icon: ClipboardList,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL, ROLES.PATIENT],
  },
  {
    key: 'professionals',
    label: 'Profesionales',
    path: '/profesionales',
    icon: Stethoscope,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA],
  },
  {
    key: 'patients',
    label: 'Pacientes',
    path: '/pacientes',
    icon: UserRound,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL],
  },
  {
    key: 'graphs',
    label: 'Estadísticas',
    path: '/graficos',
    icon: PieChart,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA, ROLES.PROFESSIONAL],
  },
  {
    key: 'profile',
    label: 'Mi perfil',
    path: '/profile',
    icon: UserCircle,
  },
  {
    key: 'catalogs',
    label: 'Catálogos',
    path: '/catalogos',
    icon: Boxes,
    roles: [ROLES.ADMIN, ROLES.SECRETARIA],
    children: [
      {
        key: 'catalogs-professions',
        label: 'Profesiones',
        path: '/catalogos/profesiones',
        icon: Briefcase,
      },
      {
        key: 'catalogs-holidays',
        label: 'Feriados',
        path: '/catalogos/feriados',
        icon: CalendarHeart,
      },
    ],
  },
  {
    key: 'settings',
    label: 'Configuración',
    path: '/settings',
    icon: Settings,
    roles: [ROLES.ADMIN],
    children: [
      {
        key: 'settings-users',
        label: 'Usuarios',
        path: '/settings/users',
        icon: Users,
      },
      {
        key: 'settings-roles',
        label: 'Roles',
        path: '/settings/roles',
        icon: ShieldCheck,
      },
      {
        key: 'settings-security-monitor',
        label: 'Seguridad',
        path: '/settings/security-monitor',
        icon: Activity,
      },
      {
        key: 'settings-activity',
        label: 'Actividad',
        path: '/settings/activity',
        icon: TrendingUp,
      },
    ],
  },
]

// First nav item (in menu order) the given role can actually open — used to
// decide where to land right after login, and as the fallback for any
// unmatched route, instead of hardcoding "/dashboard" (which not every role
// can access, e.g. Profesional/Paciente/Usuario since Dashboard became
// Admin/Secretaria-only).
export function getDefaultRouteForRole(menuSections: string[]): string {
  for (const item of NAV_ITEMS) {
    if (!item.roles || menuSections.includes(item.key)) return item.path
    const child = item.children?.find((value) => !value.roles || menuSections.includes(value.key))
    if (child) return child.path
  }
  return '/profile'
}
